import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { AuthService } from './core/services/auth';
import { LocalStorageService } from './services/local-storage-service';
import { LOCALSTORAGEKEYS } from '../models/enum';
import { switchMap, take, catchError, filter, tap, distinctUntilChanged } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { User } from '../models/user.model';
import { Router, NavigationEnd } from '@angular/router';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NzLayoutModule, NzMenuModule],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  constructor(private authService: AuthService,
    private auth: Auth,
    private lsService: LocalStorageService,
    private router: Router) { }
  private readonly freeRoutes = ['/', '/auth', '/roles', '/about', '/salaries', '/companies'];
  private routerSub!: Subscription;

  ngOnInit(): void {
    this.profileInitialization();
    this.initializeAuth();
  }

  private initializeAuth(): void {

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      distinctUntilChanged((prev, curr) => prev.url === curr.url)
    ).subscribe((event: NavigationEnd) => {
      this.handleRouteChange(event.url);
    })

  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private IsFreeRoute(url: string) {
    return this.freeRoutes.some(route => url.startsWith(route))
  }

  private handleRouteChange(url: string) {
    if (this.IsFreeRoute(url)) {
      return;
    }

    this.authService.isLoggedIn().pipe(
      take(1),
      switchMap(isLoggedIn => {
        if (!isLoggedIn) {
          return this.handleUnauthenticated();
        }
        return this.ensureUserData();
      }),
      catchError((error) => {
        console.error('Auth guard error:', error);
        return this.handleError();
      })
    ).subscribe({
      next: (user) => {
        // User data is now available in authService
        if (!user) {
          // Handle case where user couldn't be fetched
          this.router.navigate(['/auth/login']);
        }
      },
      error: () => {
        // Error already handled in catchError
      }
    });
  }

  private handleUnauthenticated() {
    this.authService.currentUserSubject.next(null);
    this.auth.signOut();
    this.router.navigate(['/'])
    return of(null)
  }


  private ensureUserData() {
    return this.authService.userObservable.pipe(
      filter(user => user != null),
      take(1),
      switchMap(user => {

        if (user) {
          return of(user)
        }

        return this.fetchAndSetUser().pipe(
          tap(user => {
            if (!user) {
              throw new Error('Failed to fetch user profile')
            }
          })
        )
      })
    );
  }

  private profileInitialization() {
    onAuthStateChanged(this.auth, user => {
      if (user) {
        this.authService.userObservable.subscribe({
          next: (p) => {
            if (!p) {
              this.fetchAndSetUser().subscribe()
            }
          }
        })
      }
    })
  }



  private fetchAndSetUser() {
    return this.authService.getUserProfile().pipe(
      switchMap(response => {
        const user = {
          id: response.id,
          account_type: response.account_type,
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email,
          profile_image: response.profile_image
        };
        this.authService.currentUserSubject.next(user);
        return of(user);
      }),
      catchError(error => {

        return of(null);
      })
    );
  }

  private handleError() {
    this.authService.currentUserSubject.next(null);
    this.authService.signOut();
    this.router.navigate(['/auth/login']);
    return of(null);
  }

}


